import type React from 'react'
import type { JSX } from 'react'
import type { CoversList } from 'blocks/Cards/Card.h'
import type { ITVShowDescriptionProps } from 'blocks/Description/Templates'
import type { IPopupProps } from '../Popup'

import { useEffect, useMemo, useState } from 'react'
import { FallbackImage } from 'atoms/FallbackImage'
import { ImagePoster } from 'atoms/ImagePoster'
import { PartnerProgramCoverDesktop } from 'atoms/PartnerProgramCover'
import { TVShowDescription } from 'blocks/Description/Templates'
import { usePoster } from 'utils/hooks'
import { COLOR } from 'vars/index'
import { Popup } from '../Popup'
import { PopupColumnGap, PopupRowGap } from '../Popup.h'
import { LogoWrapperSC, PopupTitle, PurchasedPopupSC } from '../Popup.styled'

export interface IPurchasedPopup extends Omit<IPopupProps, 'children'> {
  title?: ITVShowDescriptionProps['title']
  description?: ITVShowDescriptionProps['description']
  labels?: ITVShowDescriptionProps['list']
  posterUrl?: string | undefined
  channelPosterUrl?: string | undefined
  headerBackground?: COLOR
  popupBackground?: COLOR
  children: React.ReactNode
  isOpened?: boolean
  width?: string
  poster?: CoversList
  bundlePoster?: boolean
  isCustomFallbackImage?: boolean
}

export const PurchasedPopup = ({
  title = '',
  description = '',
  labels = [],
  width = '720px',
  rowGap = PopupRowGap.INDENT_60,
  columnGap = PopupColumnGap.INDENT_20,
  posterUrl,
  headerBackground = COLOR.OVERLAY_PRESSED,
  popupBackground = COLOR.SURFACE_DARK_4,
  children,
  poster,
  isOpened = false,
  channelPosterUrl,
  bundlePoster,
  isCustomFallbackImage,
  ...rest
}: IPurchasedPopup): JSX.Element | null => {
  const [isLoaded, setIsLoaded] = useState(false)
  const Poster = usePoster(description, poster)
  const [popupHeaderHeight, setPopupHeaderHeight] = useState(0)

  useEffect(() => {
    if (bundlePoster || posterUrl || channelPosterUrl) setIsLoaded(true)
    if (!isOpened) setIsLoaded(false)
  }, [bundlePoster, posterUrl, channelPosterUrl, isOpened])

  const posterTempate = useMemo(() => {
    return isLoaded ? (
      bundlePoster && !posterUrl ? (
        <Poster />
      ) : (
        <ImagePoster
          posterUrl={channelPosterUrl || posterUrl}
          fallbackImageColor={COLOR.TEXT_ICON_TERTIARY}
          fallbackImageBackground={COLOR.SURFACE_OPACITY_LIGHT_10}
        />
      )
    ) : isCustomFallbackImage ? (
      <PartnerProgramCoverDesktop />
    ) : (
      <FallbackImage
        color={COLOR.TEXT_ICON_TERTIARY}
        background={COLOR.SURFACE_OPACITY_LIGHT_10}
      />
    )
  }, [
    isLoaded,
    bundlePoster,
    posterUrl,
    channelPosterUrl,
    isCustomFallbackImage,
  ])

  return isOpened ? (
    <PurchasedPopupSC width={width} popupHeaderHeight={popupHeaderHeight}>
      <Popup
        width={width}
        rowGap={rowGap}
        columnGap={columnGap}
        headerBackground={headerBackground}
        popupBackground={popupBackground}
        withGradient={false}
        setPopupHeaderHeight={setPopupHeaderHeight}
        {...rest}
      >
        <PopupTitle>
          <LogoWrapperSC
            maxWidth={channelPosterUrl ? 70 : 100}
            maxHeight={channelPosterUrl ? 70 : 100}
            channelPosterUrl={channelPosterUrl}
            background={COLOR.TRANSPARENT}
          >
            {posterTempate}
          </LogoWrapperSC>
          <TVShowDescription
            title={title}
            description={description}
            list={labels}
          />
        </PopupTitle>
        {children}
      </Popup>
    </PurchasedPopupSC>
  ) : null
}
